import React from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AppButtonTone } from './app-button';

export type AppIconButtonProps = Omit<IconButtonProps, 'color'> & {
  tone?: AppButtonTone;
  title?: React.ReactNode;
};

const iconToneStyles: Record<AppButtonTone, IconButtonProps['sx']> = {
  primary: {
    border: (theme) => `1px solid ${theme.designSystem.button.primaryBorder}`,
    color: (theme) => theme.designSystem.button.primaryText,
    background: (theme) => theme.designSystem.button.primaryGradient,
    '&:hover': {
      borderColor: (theme) => theme.designSystem.button.primaryBorderHover,
      background: (theme) => theme.designSystem.button.primaryGradientHover
    },
    '&.Mui-focusVisible': {
      boxShadow: (theme) =>
        `0 0 0 2px ${alpha(theme.designSystem.button.primaryBorderHover, 0.36)}`
    }
  },
  secondary: {
    border: (theme) => `1px solid ${theme.designSystem.button.outlinedBorder}`,
    color: (theme) => theme.designSystem.button.outlinedText,
    background: (theme) =>
      `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackground}, ${theme.designSystem.button.outlinedBackgroundHover})`,
    boxShadow: (theme) =>
      `0 0 0 1px ${alpha(theme.designSystem.button.outlinedBorder, 0.55)}`,
    '&:hover': {
      borderColor: (theme) => theme.designSystem.button.outlinedBorderHover,
      background: (theme) =>
        `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackgroundHover}, ${theme.designSystem.button.outlinedBackground})`,
      boxShadow: (theme) =>
        `0 0 0 1px ${alpha(theme.designSystem.button.outlinedBorderHover, 0.45)}`
    },
    '&.Mui-focusVisible': {
      boxShadow: (theme) =>
        `0 0 0 2px ${alpha(theme.designSystem.button.outlinedBorderHover, 0.35)}`
    }
  },
  danger: {
    border: (theme) => `1px solid ${theme.designSystem.button.dangerBorder}`,
    color: (theme) => theme.designSystem.button.dangerText,
    background: (theme) => theme.designSystem.button.dangerGradient,
    '&:hover': {
      borderColor: (theme) => theme.designSystem.button.dangerBorderHover,
      background: (theme) => theme.designSystem.button.dangerGradientHover
    },
    '&.Mui-focusVisible': {
      boxShadow: (theme) =>
        `0 0 0 2px ${alpha(theme.designSystem.button.dangerBorderHover, 0.36)}`
    }
  },
  ghost: {
    border: (theme) =>
      `1px solid ${theme.designSystem.button.primaryBorderHover}`,
    color: (theme) => theme.designSystem.button.outlinedText,
    background: (theme) =>
      `linear-gradient(160deg, ${theme.designSystem.button.ghostBackground}, ${theme.designSystem.button.outlinedBackground})`,
    boxShadow: (theme) =>
      `0 0 0 1px ${alpha(theme.designSystem.button.primaryBorder, 0.3)}`,
    '&:hover': {
      borderColor: (theme) => theme.designSystem.button.primaryBorderHover,
      color: (theme) => theme.designSystem.button.primaryText,
      background: (theme) =>
        `linear-gradient(160deg, ${theme.designSystem.button.outlinedBackground}, ${theme.designSystem.button.outlinedBackgroundHover})`,
      boxShadow: (theme) =>
        `0 0 0 1px ${alpha(theme.designSystem.button.primaryBorderHover, 0.4)}`
    },
    '&.Mui-focusVisible': {
      boxShadow: (theme) =>
        `0 0 0 2px ${alpha(theme.designSystem.button.primaryBorderHover, 0.32)}`
    }
  }
};

export const AppIconButton: React.FC<AppIconButtonProps> = ({
  tone = 'ghost',
  title,
  size = 'small',
  sx,
  children,
  ...props
}) => {
  const button = (
    <IconButton
      {...props}
      size={size}
      sx={[
        {
          borderRadius: 2,
          transition:
            'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
          backdropFilter: tone === 'ghost' ? undefined : 'blur(3px)',
          '&:active': {
            transform: 'translateY(0.5px)'
          },
          '&.MuiIconButton-sizeSmall': {
            p: 0.55,
            '& .MuiSvgIcon-root': {
              fontSize: '1.1rem'
            }
          },
          '&.MuiIconButton-sizeMedium': {
            p: 0.9,
            '& .MuiSvgIcon-root': {
              fontSize: '1.4rem'
            }
          },
          '&.MuiIconButton-sizeLarge': {
            p: 1,
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem'
            }
          },
          '&.Mui-disabled, &.Mui-disabled:hover': {
            color: (theme) => theme.designSystem.button.disabledText,
            border: (theme) =>
              `1px solid ${theme.designSystem.button.disabledBorder}`,
            borderColor: (theme) => theme.designSystem.button.disabledBorder,
            background: (theme) => theme.designSystem.button.disabledBackground,
            backgroundColor: (theme) =>
              theme.designSystem.button.disabledBackground,
            cursor: 'not-allowed',
            pointerEvents: 'auto',
            boxShadow: 'none',
            opacity: 1,
            transform: 'none'
          }
        },
        iconToneStyles[tone],
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])
      ]}
    >
      {children}
    </IconButton>
  );

  if (!title) return button;

  return <Tooltip title={title}>{button}</Tooltip>;
};
